import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Brain, Plus, Link2, Trash2, ArrowLeft, Copy, CheckCircle2, Send, Pencil } from 'lucide-react';

export default function EvaluationManager({ user, onLogout }) {
  const navigate = useNavigate();
  const [evaluations, setEvaluations] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [copiedLink, setCopiedLink] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingEvaluation, setEditingEvaluation] = useState(null);

  const [newEvaluation, setNewEvaluation] = useState({
    name: '',
    description: '',
    sector_id: 'none',
    assessment_id: 'none'
  });

  const [editEvaluation, setEditEvaluation] = useState({
    sector_id: 'none',
    assessment_id: 'none'
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      console.log(API);
      const [evalResp, sectorResp, assessResp] = await Promise.all([
        axios.get(`${API}/evaluations`, config),
        axios.get(`${API}/sectors`, config),
        axios.get(`${API}/assessments`, config)
      ]);

      setEvaluations(evalResp.data);
      setSectors(sectorResp.data);
      setAssessments(assessResp.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEvaluation = async (e) => {
    e.preventDefault();

    if (!newEvaluation.name) {
      toast.error('Nome é obrigatório');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const response = await axios.post(`${API}/evaluations`, newEvaluation, config);
      
      toast.success('✅ Link de avaliação criado com sucesso!');
      setEvaluations([...evaluations, response.data]);
      setDialogOpen(false);
      setNewEvaluation({ name: '', description: '', sector_id: 'none', assessment_id: 'none' });

      // Show the link
      setCopiedLink(null);
      setTimeout(() => {
        const linkElement = document.getElementById(`link-${response.data.id}`);
        if (linkElement) {
          linkElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);

    } catch (error) {
      console.error('Error creating evaluation:', error);
      toast.error('Erro ao criar avaliação');
    }
  };

  const openEditDialog = (evaluation) => {
    setEditingEvaluation(evaluation);
    setEditEvaluation({
      sector_id: evaluation.sector_id || 'none',
      assessment_id: evaluation.assessment_id || 'none'
    });
    setEditDialogOpen(true);
  };

  const handleUpdateEvaluation = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const payload = {
        sector_id: editEvaluation.sector_id === 'none' ? '' : editEvaluation.sector_id,
        assessment_id: editEvaluation.assessment_id === 'none' ? '' : editEvaluation.assessment_id
      };

      const response = await axios.patch(`${API}/evaluations/${editingEvaluation.id}`, payload, config);

      toast.success('✅ Avaliação vinculada atualizada com sucesso!');
      setEvaluations(evaluations.map(ev => ev.id === editingEvaluation.id ? response.data : ev));
      setEditDialogOpen(false);
      setEditingEvaluation(null);

    } catch (error) {
      console.error('Error updating evaluation:', error);
      toast.error('Erro ao atualizar avaliação');
    }
  };

  const handleDeleteEvaluation = async (id) => {
    if (!confirm('Tem certeza que deseja deletar esta avaliação?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      await axios.delete(`${API}/evaluations/${id}`, config);
      
      toast.success('Avaliação deletada com sucesso');
      setEvaluations(evaluations.filter(e => e.id !== id));

    } catch (error) {
      console.error('Error deleting evaluation:', error);
      const errorMessage = error.response?.data?.detail || 'Erro ao deletar avaliação';
      toast.error(errorMessage);
    }
  };

  const copyToClipboard = (publicLink, evalId) => {
    const fullUrl = `${window.location.origin}/public/register/${publicLink}`;
    
    // Try modern clipboard API first
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(fullUrl)
        .then(() => {
          toast.success('Link copiado!');
          setCopiedLink(evalId);
          setTimeout(() => setCopiedLink(null), 2000);
        })
        .catch(() => {
          // Fallback to older method
          fallbackCopyToClipboard(fullUrl, evalId);
        });
    } else {
      // Use fallback method
      fallbackCopyToClipboard(fullUrl, evalId);
    }
  };

  const fallbackCopyToClipboard = (text, evalId) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      document.execCommand('copy');
      toast.success('Link copiado!');
      setCopiedLink(evalId);
      setTimeout(() => setCopiedLink(null), 2000);
    } catch (err) {
      toast.error('Erro ao copiar. Por favor, copie manualmente.');
    }
    
    document.body.removeChild(textArea);
  };

  const getSectorName = (sectorId) => {
    const sector = sectors.find(s => s.id === sectorId);
    return sector?.name || 'Não especificado';
  };

  const getAssessmentName = (assessmentId) => {
    const assessment = assessments.find(a => a.id === assessmentId);
    return assessment?.name || 'Padrão (20 perguntas)';
  };

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Carregando...</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-blue-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="text-gray-600 hover:text-[#f97316]"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Voltar
            </Button>
            <div className="flex items-center gap-2">
              <Brain className="w-8 h-8 text-[#f97316]" />
              <h1 className="text-2xl font-bold text-[#f97316]">Gerenciar Avaliações</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-600">{user?.name}</span>
            <Button variant="outline" size="sm" onClick={onLogout}>
              Sair
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Info Card */}
        <Card className="mb-6 border-l-4 border-[#2563eb] bg-blue-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <div className="bg-[#2563eb] p-2 rounded-full">
                <Link2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-[#2563eb] mb-1">💬 Dica da Cíntia:</h3>
                <p className="text-sm text-gray-700">
                  Crie links únicos de avaliação e compartilhe com candidatos! Eles poderão se auto-registrar e iniciar o teste DISC imediatamente.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Create Button */}
        <div className="mb-6">
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#f97316] hover:bg-[#ea580c] text-white">
                <Plus className="w-5 h-5 mr-2" />
                Criar Nova Avaliação
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle className="text-[#f97316]">Criar Nova Avaliação</DialogTitle>
                <DialogDescription>
                  Configure uma nova avaliação e gere um link único para candidatos
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateEvaluation} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nome da Avaliação *</Label>
                  <Input
                    id="name"
                    placeholder="Ex: Processo Seletivo Suporte N1"
                    value={newEvaluation.name}
                    onChange={(e) => setNewEvaluation({ ...newEvaluation, name: e.target.value })}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Descrição (opcional)</Label>
                  <Textarea
                    id="description"
                    placeholder="Descrição adicional sobre esta avaliação"
                    value={newEvaluation.description}
                    onChange={(e) => setNewEvaluation({ ...newEvaluation, description: e.target.value })}
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="sector">Setor (opcional)</Label>
                  <Select
                    value={newEvaluation.sector_id}
                    onValueChange={(value) => setNewEvaluation({ ...newEvaluation, sector_id: value === 'none' ? '' : value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione um setor" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nenhum (candidato escolhe)</SelectItem>
                      {sectors.map((sector) => (
                        <SelectItem key={sector.id} value={sector.id}>
                          {sector.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="assessment">Avaliação/Questões (opcional)</Label>
                  <Select
                    value={newEvaluation.assessment_id}
                    onValueChange={(value) => setNewEvaluation({ ...newEvaluation, assessment_id: value === 'none' ? '' : value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione questões" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Padrão (20 perguntas)</SelectItem>
                      {assessments.map((assessment) => (
                        <SelectItem key={assessment.id} value={assessment.id}>
                          {assessment.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button type="submit" className="bg-[#f97316] hover:bg-[#ea580c] flex-1">
                    <Link2 className="w-4 h-4 mr-2" />
                    Criar Link de Avaliação
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setDialogOpen(false)}
                  >
                    Cancelar
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-[#2563eb]">Editar Vínculo da Avaliação</DialogTitle>
              <DialogDescription>
                Altere o setor e/ou a avaliação (questões) usados por este link: {editingEvaluation?.name}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUpdateEvaluation} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="edit-sector">Setor (opcional)</Label>
                <Select
                  value={editEvaluation.sector_id}
                  onValueChange={(value) => setEditEvaluation({ ...editEvaluation, sector_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione um setor" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nenhum (candidato escolhe)</SelectItem>
                    {sectors.map((sector) => (
                      <SelectItem key={sector.id} value={sector.id}>
                        {sector.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-assessment">Avaliação/Questões (opcional)</Label>
                <Select
                  value={editEvaluation.assessment_id}
                  onValueChange={(value) => setEditEvaluation({ ...editEvaluation, assessment_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione questões" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Padrão (20 perguntas)</SelectItem>
                    {assessments.map((assessment) => (
                      <SelectItem key={assessment.id} value={assessment.id}>
                        {assessment.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="submit" className="bg-[#2563eb] hover:bg-[#1d4ed8] flex-1">
                  Salvar Alterações
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setEditDialogOpen(false)}
                >
                  Cancelar
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Evaluations List */}
        <div className="grid gap-4">
          {evaluations.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Link2 className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">
                  Cíntia ainda não encontrou avaliações. Que tal criar sua primeira?
                </p>
              </CardContent>
            </Card>
          ) : (
            evaluations.map((evaluation) => (
              <Card
                key={evaluation.id}
                id={`link-${evaluation.id}`}
                className="border-2 border-gray-200 hover:border-[#f97316]/50 transition-colors"
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-xl text-[#2563eb]">{evaluation.name}</CardTitle>
                      {evaluation.description && (
                        <CardDescription className="mt-1">{evaluation.description}</CardDescription>
                      )}
                      <div className="flex flex-wrap gap-3 mt-3 text-sm text-gray-600">
                        <span className="bg-blue-100 px-3 py-1 rounded-full">
                          Setor: {getSectorName(evaluation.sector_id)}
                        </span>
                        <span className="bg-purple-100 px-3 py-1 rounded-full">
                          {getAssessmentName(evaluation.assessment_id)}
                        </span>
                        <span className="bg-gray-100 px-3 py-1 rounded-full">
                          Criado em: {new Date(evaluation.created_at).toLocaleDateString('pt-BR')}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEditDialog(evaluation)}
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      >
                        <Pencil className="w-5 h-5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteEvaluation(evaluation.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-5 h-5" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <Label className="text-sm font-semibold text-gray-700 mb-2 block">
                      🔗 Link Público para Candidatos:
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        value={`${window.location.origin}/public/register/${evaluation.public_link}`}
                        readOnly
                        className="font-mono text-sm bg-white"
                      />
                      <Button
                        onClick={() => copyToClipboard(evaluation.public_link, evaluation.id)}
                        className={`${
                          copiedLink === evaluation.id
                            ? 'bg-green-600 hover:bg-green-700'
                            : 'bg-[#2563eb] hover:bg-[#1d4ed8]'
                        }`}
                      >
                        {copiedLink === evaluation.id ? (
                          <>
                            <CheckCircle2 className="w-4 h-4 mr-2" />
                            Copiado!
                          </>
                        ) : (
                          <>
                            <Copy className="w-4 h-4 mr-2" />
                            Copiar
                          </>
                        )}
                      </Button>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Compartilhe este link com candidatos via WhatsApp, email ou redes sociais
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
